import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  /** Smallest accepted value. Defaults to 1. */
  min?: number;
  /** Largest accepted value. Defaults to 100000 (the API limit). */
  max?: number;
  /** Amount added/removed by the +/- buttons. Defaults to 1. */
  step?: number;
  /** Decimal places kept when committing a typed value. Defaults to 2. */
  decimals?: number;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Accessible name for the group, e.g. "X-ray quantity". */
  ariaLabel?: string;
  className?: string;
}

/** Rounds without the floating-point drift of a plain toFixed/Number round-trip. */
function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function formatQuantity(value: number, decimals: number): string {
  const rounded = roundTo(value, decimals);
  return String(rounded);
}

/**
 * Compact quantity control for cart lines. Typed values are committed on blur
 * or Enter (so decimals like "1.5" can be entered), then clamped to the
 * service's bounds. Buttons step immediately.
 */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = 100000,
  step = 1,
  decimals = 2,
  disabled = false,
  size = 'md',
  ariaLabel = 'Quantity',
  className,
}: QuantityStepperProps) {
  const [draft, setDraft] = useState(() => formatQuantity(value, decimals));

  // Keep the input in step with external updates (e.g. a pricing rule resolving
  // a typed quantity to the next billable unit).
  useEffect(() => {
    setDraft(formatQuantity(value, decimals));
  }, [value, decimals]);

  const commit = (raw: string) => {
    const parsed = Number(raw);
    const base = Number.isFinite(parsed) ? parsed : value;
    const next = Math.min(max, Math.max(min, roundTo(base, decimals)));
    setDraft(formatQuantity(next, decimals));
    if (next !== value) onChange(next);
  };

  const nudge = (direction: 1 | -1) => {
    if (disabled) return;
    // Start from the typed draft when it is valid, so clicking +/- right after
    // typing does not discard the uncommitted value.
    const parsed = Number(draft);
    const base = Number.isFinite(parsed)
      ? Math.min(max, Math.max(min, roundTo(parsed, decimals)))
      : value;
    const next = Math.min(max, Math.max(min, roundTo(base + direction * step, decimals)));
    setDraft(formatQuantity(next, decimals));
    if (next !== value) onChange(next);
  };

  const buttonSize = size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';
  const inputHeight = size === 'sm' ? 'h-6' : 'h-7';
  const inputWidth = size === 'sm' ? 'w-9' : 'w-11';
  const iconSize = size === 'sm' ? 11 : 12;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-surface',
        disabled && 'opacity-50',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => nudge(-1)}
        disabled={disabled || value <= min}
        className={cn(
          'flex items-center justify-center text-text-muted transition-colors hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted',
          buttonSize,
        )}
        title="Decrease quantity"
        aria-label={`Decrease ${ariaLabel.toLowerCase()}`}
      >
        <Minus size={iconSize} />
      </button>
      <input
        type="text"
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit((event.target as HTMLInputElement).value);
            (event.target as HTMLInputElement).blur();
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          'border-x border-border bg-transparent text-center text-xs font-semibold tabular-nums text-text focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary/40',
          inputHeight,
          inputWidth,
        )}
      />
      <button
        type="button"
        onClick={() => nudge(1)}
        disabled={disabled || value >= max}
        className={cn(
          'flex items-center justify-center text-text-muted transition-colors hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted',
          buttonSize,
        )}
        title="Increase quantity"
        aria-label={`Increase ${ariaLabel.toLowerCase()}`}
      >
        <Plus size={iconSize} />
      </button>
    </div>
  );
}
